import BaseService from './BaseService'

const NotificationService = {}

// Send push notification to all users
NotificationService.sendPushNotificationToAllUsers = function (data) {
    return BaseService({
        url: '/notifications/send-to-all',
        method: 'post',
        data,
    })
}

// Send test notification to current user
NotificationService.sendTestNotification = function (data) {
    return BaseService({
        url: '/notifications/test',
        method: 'post',
        data,
    })
}

// Send test notification to specific user
NotificationService.sendTestNotificationToUser = function (userId, data) {
    return BaseService({
        url: `/notifications/test/user/${userId}`,
        method: 'post',
        data,
    })
}

// Get notifications list
NotificationService.getNotifications = function (params) {
    return BaseService({
        url: '/notifications',
        method: 'get',
        params,
    })
}

// Mark notifications as read
NotificationService.markAsRead = function (data) {
    return BaseService({
        url: '/notifications/mark-read',
        method: 'patch',
        data,
    })
}

// Get notification preferences
NotificationService.getNotificationPreferences = function () {
    return BaseService({
        url: '/notifications/preferences',
        method: 'get',
    })
}

// Update notification preferences
NotificationService.updateNotificationPreferences = function (data) {
    return BaseService({
        url: '/notifications/preferences',
        method: 'put',
        data,
    })
}

// ======================
// DEVICE TOKEN MANAGEMENT
// ======================

// Register device token
NotificationService.registerDeviceToken = function (token, deviceType = 'web', deviceInfo = {}) {
    return BaseService({
        url: '/device-tokens/register',
        method: 'post',
        data: { token, deviceType, deviceInfo },
    })
}

// Unregister device token
NotificationService.unregisterDeviceToken = function (token) {
    return BaseService({
        url: '/device-tokens/unregister',
        method: 'post',
        data: { token },
    })
}

// Get current device token from storage
NotificationService.getCurrentDeviceToken = function () {
    return localStorage.getItem('dashboardDeviceToken') || sessionStorage.getItem('dashboardDeviceToken');
}

// Store device token
NotificationService.storeDeviceToken = function (token) {
    localStorage.setItem('dashboardDeviceToken', token);
    sessionStorage.setItem('dashboardDeviceToken', token);
}

// Remove device token from storage
NotificationService.removeDeviceToken = function () {
    localStorage.removeItem('dashboardDeviceToken');
    sessionStorage.removeItem('dashboardDeviceToken');
}

// Register current device token — now delegates to PushNotificationService
// for real Web Push via VAPID keys. Falls back to the legacy random-token
// approach only when push is not supported.
NotificationService.registerCurrentDeviceToken = async function (deviceType = 'web', deviceInfo = {}) {
    try {
        console.log('[DASHBOARD NOTIFICATION SERVICE] Attempting to register device token...');

        // Lazy-import to avoid circular deps
        const PushNotificationService = (await import('./PushNotificationService')).default;

        if (PushNotificationService.isSupported()) {
            const ok = await PushNotificationService.subscribe();
            if (ok) {
                console.log('[DASHBOARD NOTIFICATION SERVICE] Web Push subscription registered');
                return { status: 'success', method: 'webpush' };
            }
            // If subscribe returns false (e.g. permission denied) fall through
            console.log('[DASHBOARD NOTIFICATION SERVICE] Web Push subscribe returned false, using fallback');
        }

        // Fallback — generate a simple token (for browsers without Push API)
        const deviceToken = `dashboard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const response = await this.registerDeviceToken(deviceToken, deviceType, {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            ...deviceInfo,
        });

        this.storeDeviceToken(deviceToken);

        console.log('[DASHBOARD NOTIFICATION SERVICE] Fallback device token registered');
        return response;
    } catch (error) {
        console.log('[DASHBOARD NOTIFICATION SERVICE] Failed to register device token:', error);
        throw error;
    }
}

// Unregister current device token — also tears down Web Push subscription
NotificationService.unregisterCurrentDeviceToken = async function () {
    try {
        console.log('[DASHBOARD NOTIFICATION SERVICE] Attempting to unregister current device token...');

        // Unsubscribe Web Push first
        try {
            const PushNotificationService = (await import('./PushNotificationService')).default;
            if (PushNotificationService.isSubscribed()) {
                await PushNotificationService.unsubscribe();
                console.log('[DASHBOARD NOTIFICATION SERVICE] Web Push unsubscribed');
            }
        } catch (pushError) {
            console.log('[DASHBOARD NOTIFICATION SERVICE] Web Push unsubscribe error (non-fatal):', pushError);
        }

        // Also handle legacy fallback token
        const currentToken = this.getCurrentDeviceToken();

        if (currentToken) {
            await this.unregisterDeviceToken(currentToken);
            this.removeDeviceToken();
            console.log('[DASHBOARD NOTIFICATION SERVICE] Legacy device token unregistered');
        }
    } catch (error) {
        console.log('[DASHBOARD NOTIFICATION SERVICE] Failed to unregister device token:', error);
        // Don't throw error to avoid breaking logout flow
    }
}

export default NotificationService 